import React, { useState } from "react";
import { Divider, Flex, Text, useToken } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import usePathPickBasketSelection from "@/features/pathpick/features/basket/hooks/usePathPickBasketSelection";
import useRemovePathPickBasketItems from "@/features/pathpick/features/basket/hooks/useRemovePathPickBasketItems";
import useSyncRequestFlow from "@/features/pathpick/features/basket/hooks/useSyncRequestFlow";
import usePathPickBasketBulkOrder from "@/features/pathpick/features/basket/hooks/usePathPickBasketBulkOrder";
import PathPickUsageCard from "@/features/pathpick/components/usage/PathPickUsageCard";
import PathPickUsageModal from "@/features/pathpick/components/usage/PathPickUsageModal";
import PathPickBasketProductSyncPanel from "@/features/pathpick/features/basket/components/sync/PathPickBasketProductSyncPanel";
import PathPickBasketProductOrderPanel from "@/features/pathpick/features/basket/components/order/PathPickBasketProductOrderPanel";
import PathPickBasketSelectionBar from "@/features/pathpick/features/basket/components/action-panel/PathPickBasketSelectionBar";

// 연동/다운로드·주문 액션 패널 — 조합만 담당한다.
// 플로우 상태/모달은 각 하위 패널(SyncPanel/OrderPanel)이 소유하고,
// 교차 상태(선택/주문 payload/연동 단계)는 Formik 컨텍스트로 공유한다.
function PathPickBasketActionPanel() {
  const { t } = useI18nHelper();
  const [hairlineDefault] = useToken("shadows", ["hairlineDefault"]);
  // 플로팅 바의 일괄 주문 — 주문 행과 같은 훅이라 한도 판정·로딩 상태를 공유한다.
  const {
    startBulkOrder,
    isLoading: isOrderLoading,
    isOverLimit,
  } = usePathPickBasketBulkOrder();
  const { selectedProducts, selectedCount, soldOutCount, removeSoldOut } =
    usePathPickBasketSelection();
  const removeItems = useRemovePathPickBasketItems();
  // 플로팅 바도 패널과 동일한 플로우를 트리거한다(단계 상태는 컨텍스트 공유).
  const { startApiSync, startFileRequest } = useSyncRequestFlow();

  const [isUsageModalOpen, setIsUsageModalOpen] = useState<boolean>(false);

  const hasSelection = selectedCount > 0;

  const handleDeleteSelected = () => {
    removeItems(
      Array.from(selectedProducts.values(), ({ id, type }) => ({ id, type }))
    );
  };

  return (
    <>
      <Flex flexDirection={"column"} gap={"12px"}>
        {/* 좁은 폭에서 카드는 내부까지 세로로 스택되며 상단을 150px 넘게 잡아먹어
            신청·주문 패널을 밀어낸다. 모바일에서는 카드를 감추고 사용량 버튼 + 모달로
            같은 내용을 본다(등록 신청 내역 페이지와 같은 방식).
            조건부 언마운트가 아니라 CSS로 숨기는 이유는 카드의 usage 쿼리가 suspense라,
            모달을 처음 열 때 페이지 전체가 suspend되는 것을 막기 위함이다. */}
        <Flex
          display={{ base: "none", md: "flex" }}
          gap={"12px"}
          alignItems={"center"}
        >
          <PathPickUsageCard />
        </Flex>
        <PathPickUsageModal.TriggerButton
          onClick={() => setIsUsageModalOpen(true)}
        />

        {/* 품절 안내 — 선택에 품절이 섞여 있는 동안만 신청/주문 카드 위에 노출한다. */}
        {soldOutCount > 0 && (
          <Flex
            padding={"12px"}
            borderRadius={"8px"}
            border={"0.5px solid #EE7D7A"}
            background={"#FFEEEE"}
          >
            <Text textStyle={"body-sm-medium"} color={"#CC1D1D"}>
              {t("path_pick:basket.sold_out_notice.panel", {
                count: soldOutCount,
              })}
            </Text>
          </Flex>
        )}

        <Flex
          flexDirection={"column"}
          boxShadow={hairlineDefault}
          borderRadius={"8px"}
          background={"#FFFFFF"}
        >
          <PathPickBasketProductSyncPanel />
          <Divider borderColor={"#D4D4D1"} />
          <PathPickBasketProductOrderPanel />
        </Flex>
      </Flex>

      {isUsageModalOpen && (
        <PathPickUsageModal isOpen onClose={() => setIsUsageModalOpen(false)} />
      )}

      {hasSelection && (
        <PathPickBasketSelectionBar
          selectedCount={selectedCount}
          soldOutCount={soldOutCount}
          onExcludeSoldOut={removeSoldOut}
          onDeleteSelected={handleDeleteSelected}
          onApiSync={startApiSync}
          onFileRequest={startFileRequest}
          isOrderOverLimit={isOverLimit}
          isOrderLoading={isOrderLoading}
          onOrder={startBulkOrder}
        />
      )}
    </>
  );
}

export default PathPickBasketActionPanel;
